/**
 * Team Selector Component
 *
 * Provides a dropdown for selecting and switching between teams
 */

import React from 'react'
import { ChevronDown } from 'lucide-react'
import type { Team } from 'src/store/todo/types'

type TeamSelectorProps = {
  /** Currently selected team ID */
  selectedTeamId: string | null
  /** Array of available teams */
  teams: Team[]
  /** Callback when a team is selected */
  onTeamSelect: (teamId: string) => void
}

/**
 * TeamSelector component for switching between teams
 *
 * @example
 * <TeamSelector
 *   selectedTeamId={teamId}
 *   teams={allTeams}
 *   onTeamSelect={handleTeamSelect}
 * />
 */
const TeamSelector: React.FC<TeamSelectorProps> = ({
  selectedTeamId,
  teams,
  onTeamSelect,
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false)

  const handleSelect = (teamId: string) => {
    onTeamSelect(teamId)
    setShowDropdown(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
      >
        Switch Team
        <ChevronDown size={16} />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {teams.map((team) => (
            <button
              key={team.id}
              onClick={() => handleSelect(team.id)}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                selectedTeamId === team.id ? 'bg-green-50' : ''
              }`}
            >
              <div className="font-semibold text-gray-900">{team.teamName}</div>
              <div className="text-xs text-gray-600">{team.teamDescription}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default TeamSelector
